import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Assignment } from '../types';
import { AssignmentsAPI } from '../services/api';

type SortOption = 'default' | 'score_desc' | 'score_asc';

interface AssignmentsState {
  assignments: Assignment[];
  activeRounds: number[];
  loading: boolean;
  error: string | null;
  filter: {
    round: number | null;
    status: string | null;
  };
  sort: SortOption;
  lastFetched: number | null;
}

interface AssignmentsContextType extends AssignmentsState {
  setFilter: (filter: { round: number | null; status: string | null }) => void;
  setSort: (sort: SortOption) => void;
  sortedAssignments: Assignment[];
  isRoundActive: (round: number) => boolean;
  fetchAssignments: (force?: boolean) => Promise<void>;
  getAssignmentById: (id: number) => Assignment | undefined;
  getNavigation: (currentId: number) => {
    prev: Assignment | null;
    next: Assignment | null;
    nextPending: Assignment | null;
    current: number;
    total: number;
  };
  updateAssignmentStatus: (id: number, status: Assignment['status']) => void;
}

const AssignmentsContext = createContext<AssignmentsContextType | null>(null);

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const AssignmentsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AssignmentsState>({
    assignments: [],
    activeRounds: [],
    loading: false,
    error: null,
    filter: {
      round: 1, // Default to Round 1
      status: null,
    },
    sort: 'default',
    lastFetched: null,
  });

  const setFilter = useCallback((filter: { round: number | null; status: string | null }) => {
    setState(prev => ({ ...prev, filter }));
  }, []);

  const setSort = useCallback((sort: SortOption) => {
    setState(prev => ({ ...prev, sort }));
  }, []);

  const sortedAssignments = React.useMemo(() => {
    if (state.sort === 'default') return state.assignments;

    return [...state.assignments].sort((a, b) => {
      const scoreA = a.average_score != null ? Number(a.average_score) : -1;
      const scoreB = b.average_score != null ? Number(b.average_score) : -1;

      if (state.sort === 'score_desc') return scoreB - scoreA;
      if (state.sort === 'score_asc') return scoreA - scoreB;
      return 0;
    });
  }, [state.assignments, state.sort]);

  const fetchAssignments = useCallback(async (force: boolean = false) => {
    // Check cache validity (but skip cache check if force=true)
    // Note: We cache even empty results to prevent infinite re-fetching
    const now = Date.now();
    if (!force && state.lastFetched && (now - state.lastFetched) < CACHE_DURATION) {
      return; // Use cached data (even if empty)
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await AssignmentsAPI.getList(
        state.filter.round || undefined,
        state.filter.status || undefined
      );

      if (response.success) {
        // Ensure data is always an array (even if API returns null/undefined)
        const assignments = Array.isArray(response.data) ? response.data : [];
        const activeRounds = Array.isArray((response as any).active_rounds) ? (response as any).active_rounds : [];
        setState(prev => ({
          ...prev,
          assignments: assignments,
          activeRounds: activeRounds,
          loading: false,
          error: null,
          lastFetched: Date.now(),
        }));
      } else {
        setState(prev => ({
          ...prev,
          assignments: [], // Clear assignments on error
          error: response.message || 'Failed to load assignments',
          loading: false,
        }));
      }
    } catch (err: any) {
      console.error('Failed to fetch assignments:', err);
      setState(prev => ({
        ...prev,
        assignments: [], // Clear assignments on error
        error: err.message || 'An error occurred while loading assignments',
        loading: false,
      }));
    }
  }, [state.filter.round, state.filter.status, state.lastFetched]);

  const getAssignmentById = useCallback((id: number) => {
    return state.assignments.find(a => a.id === id);
  }, [state.assignments]);

  const getNavigation = useCallback((currentId: number) => {
    const currentIndex = sortedAssignments.findIndex(a => a.id === currentId);

    // Find prev/next in list order
    const prev = currentIndex > 0 ? sortedAssignments[currentIndex - 1] : null;
    const next = currentIndex < sortedAssignments.length - 1 ? sortedAssignments[currentIndex + 1] : null;

    // Also find next PENDING assignment (for smarter navigation after scoring)
    const nextPending = sortedAssignments.find((a, idx) =>
      idx > currentIndex && a.status !== 'completed'
    ) || null;

    return {
      prev,
      next,
      nextPending, // Next assignment that hasn't been scored yet
      current: currentIndex + 1,
      total: sortedAssignments.length,
    };
  }, [sortedAssignments]);

  const isRoundActive = useCallback((round: number) => {
    return state.activeRounds.includes(round);
  }, [state.activeRounds]);

  const updateAssignmentStatus = useCallback((id: number, status: Assignment['status']) => {
    setState(prev => ({
      ...prev,
      assignments: prev.assignments.map(a =>
        a.id === id ? { ...a, status } : a
      ),
    }));
  }, []);

  return (
    <AssignmentsContext.Provider
      value={{
        ...state,
        setFilter,
        setSort,
        sortedAssignments,
        isRoundActive,
        fetchAssignments,
        getAssignmentById,
        getNavigation,
        updateAssignmentStatus,
      }}
    >
      {children}
    </AssignmentsContext.Provider>
  );
};

export const useAssignments = () => {
  const context = useContext(AssignmentsContext);
  if (!context) {
    throw new Error('useAssignments must be used within an AssignmentsProvider');
  }
  return context;
};
